'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchExploreCatalog } from '@/app/services/courseCatalogClient';
import type { ExploreCatalogSummary } from '@/app/types/interfaces';

export function useExploreCatalog(semester: string, enabled = true) {
  const [courses, setCourses] = useState<ExploreCatalogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const requestGeneration = useRef(0);
  const cache = useRef(new Map<string, ExploreCatalogSummary[]>());
  const pending = useRef(new Map<string, Promise<ExploreCatalogSummary[]>>());

  const requestCatalog = useCallback((term: string) => {
    const cached = cache.current.get(term);
    if (cached) return Promise.resolve(cached);
    const existing = pending.current.get(term);
    if (existing) return existing;

    const request = fetchExploreCatalog(term).then((data) => {
      if (!data.length) throw new Error('No catalog data is available for this semester.');
      cache.current.set(term, data);
      return data;
    }).finally(() => {
      pending.current.delete(term);
    });
    pending.current.set(term, request);
    return request;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    void requestCatalog(semester).then((data) => {
      if (generation === requestGeneration.current) setCourses(data);
    }).catch((cause) => {
      if (generation === requestGeneration.current) setError(cause instanceof Error ? cause.message : 'Course catalog could not be loaded.');
    }).finally(() => {
      if (generation === requestGeneration.current) setLoading(false);
    });
    return () => {
      if (generation === requestGeneration.current) requestGeneration.current += 1;
    };
  }, [enabled, reloadVersion, requestCatalog, semester]);

  const reload = useCallback(() => setReloadVersion((version) => version + 1), []);
  return { courses, loading, error, reload };
}
